import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-key-set-importer.js'
import './data-key-item-list.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'

import { getEditor, getRenderer, DataGrist, FetchOption } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { notify, openPopup } from '@operato/layout'
import { navigate, PageView, store } from '@operato/shell'
import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice, encodeUrlParams } from '@operato/utils'

const REPORT_TYPES = [
  {
    display: '',
    value: ''
  },
  {
    display: 'Generated',
    value: 'generated'
  },
  {
    display: 'Embed',
    value: 'embed'
  },
  {
    display: 'CustomElement',
    value: 'custom-element'
  },
  {
    display: 'Page',
    value: 'page'
  },
  {
    display: 'External URL',
    value: 'external'
  },
  {
    display: 'Jasper',
    value: 'jasper'
  },
  {
    display: 'Shiny',
    value: 'shiny'
  }
]

@customElement('data-key-set-list-page')
export class DataKeySetListPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;

        width: 100%;

        --grid-record-emphasized-background-color: #8b0000;
        --grid-record-emphasized-color: #ff6b6b;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-key-set list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-key-set',
      actions: [
        {
          title: i18next.t('button.save'),
          action: this._updateDataKeySet.bind(this),
          icon: 'save'
        },
        {
          title: i18next.t('button.delete'),
          action: this._deleteDataKeySet.bind(this),
          icon: 'delete',
          emphasis: {
            danger: true
          }
        }
      ],
      exportable: {
        name: i18next.t('title.data-key-set list'),
        data: this.exportHandler.bind(this)
      },
      importable: {
        handler: this.importHandler.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'CARD' : 'GRID')

    return html`
      <ox-grist .mode=${mode} .config=${this.gristConfig} .fetchHandler=${this.fetchHandler.bind(this)}>
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}> </ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: {
        fields: ['name', 'description'],
        details: ['active', 'updatedAt']
      },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', fixed: true, multiple: true },
        {
          type: 'gutter',
          gutterName: 'button',
          fixed: true,
          title: record =>
            !record ? i18next.t('button.edit-model') : !record.id ? '' : i18next.t('button.edit-model'),
          icon: record => (!record ? 'reorder' : !record.id ? '' : 'reorder'),
          iconOnly: false,
          width: 96,
          handlers: {
            click: (columns, data, column, record, rowIndex) => {
              if (!record.id) return
              const popup = openPopup(html` <data-key-item-list .dataKeySet=${record}></data-key-item-list> `, {
                backdrop: true,
                help: 'dataset/ui/data-key-item-list',
                size: 'large',
                title: i18next.t('title.data-key-item list')
              })
              popup.onclosed = () => {
                this.grist.fetch()
              }
            }
          }
        },
        {
          type: 'string',
          name: 'name',
          fixed: true,
          header: i18next.t('field.name'),
          record: {
            editable: true
          },
          filter: 'search',
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'description',
          header: i18next.t('field.description'),
          record: {
            editable: true
          },
          filter: 'search',
          width: 200
        },
        {
          type: 'checkbox',
          name: 'active',
          label: true,
          header: i18next.t('field.active'),
          record: {
            editable: true
          },
          filter: true,
          sortable: true,
          width: 60
        },
        {
          type: 'select',
          name: 'reportType',
          label: true,
          header: i18next.t('field.report-type'),
          record: {
            editable: true,
            options: REPORT_TYPES
          },
          width: 80
        },
        {
          type: 'string',
          name: 'reportView',
          header: i18next.t('field.report-view'),
          record: {
            editable: true,
            editor: function (value, column, record, rowIndex, field) {
              var type = record.reportType !== 'custom' ? 'string' : 'script'
              return getEditor(type)(value, column, record, rowIndex, field)
            },
            renderer: function (value, column, record, rowIndex, field) {
              var type = record.reportType !== 'custom' ? 'string' : 'string'
              return getRenderer(type)(value, column, record, rowIndex, field)
            }
          },
          width: 140
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          record: {
            editable: false
          },
          sortable: true,
          width: 120
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          record: {
            editable: false
          },
          sortable: true,
          width: 180
        }
      ],
      rows: {
        selectable: {
          multiple: true
        }
      },
      sorters: [
        {
          name: 'name'
        }
      ]
    }
  }

  async pageUpdated(changes, lifecycle) {
    if (this.active) {
      // do something here when this page just became as active
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataKeySets(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              active
              reportType
              reportView
              dataKeyItems {
                name
                description
                dataKey
                tKey
              }
              updater {
                id
                name
              }
              updatedAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  async _deleteDataKeySet() {
    if (confirm(i18next.t('text.sure_to_x', { x: i18next.t('text.delete') }))) {
      const ids = this.grist.selected.map(record => record.id)
      if (ids && ids.length > 0) {
        const response = await client.mutate({
          mutation: gql`
            mutation ($ids: [String!]!) {
              deleteDataKeySets(ids: $ids)
            }
          `,
          variables: {
            ids
          }
        })

        if (!response.errors) {
          this.grist.fetch()
          notify({
            message: i18next.t('text.info_x_successfully', { x: i18next.t('text.delete') })
          })
        }
      }
    }
  }

  async _updateDataKeySet() {
    let patches = this.grist.dirtyRecords
    if (patches && patches.length) {
      patches = patches.map(patch => {
        let patchField: any = patch.id ? { id: patch.id } : {}
        const dirtyFields = patch.__dirtyfields__
        for (let key in dirtyFields) {
          patchField[key] = dirtyFields[key].after
        }
        patchField.cuFlag = patch.__dirty__

        return patchField
      })

      const response = await client.mutate({
        mutation: gql`
          mutation ($patches: [DataKeySetPatch!]!) {
            updateMultipleDataKeySet(patches: $patches) {
              name
            }
          }
        `,
        variables: {
          patches
        }
      })

      if (!response.errors) {
        this.grist.fetch()
      }
    }
  }

  async exportHandler() {
    const exportTargets = this.grist.selected.length ? this.grist.selected : this.grist.dirtyData.records
    const targetFieldSet = new Set(['id', 'name', 'description', 'active', 'reportType', 'reportView'])

    return exportTargets.map(dataKeySet => {
      let tempObj = {}
      for (const field of targetFieldSet) {
        tempObj[field] = dataKeySet[field]
      }

      return tempObj
    })
  }

  async importHandler(records) {
    const popup = openPopup(
      html`
        <data-key-set-importer
          .dataKeySets=${records}
          @imported=${() => {
            history.back()
            this.grist.fetch()
          }}
        ></data-key-set-importer>
      `,
      {
        backdrop: true,
        size: 'large',
        title: i18next.t('title.import data-key-set')
      }
    )

    popup.onclosed = () => {
      this.grist.fetch()
    }
  }
}
